<template>
  <section class="n2n-register">
    <div class="n2n-register__main">
      <div class="filter-grid">
          <el-select v-model="filters.status" placeholder="상태 선택" clearable size="default" @change="handleSearch">
            <el-option
              v-for="status in statusOptions"
              :key="status.value"
              :label="status.label"
              :value="status.value"
            />
          </el-select>
          <el-select v-model="filters.groupid" placeholder="그룹 선택" clearable size="default" @change="handleSearch">
            <el-option
              v-for="group in groupOptions"
              :key="group.groupid"
              :label="groupLabel(group)"
              :value="group.groupid"
            />
          </el-select>
          <el-select
            v-model="filters.marketNumber"
            placeholder="마켓 선택"
            clearable
            size="default"
            @change="handleSearch"
          >
            <el-option
              v-for="account in naverAccounts"
              :key="account.naverMarketNumber"
              :label="naverAccountLabel(account)"
              :value="account.naverMarketNumber"
            />
          </el-select>
          <el-input
            v-model="filters.q"
            placeholder="통합 검색어 입력"
            clearable
            size="default"
            @keyup.enter="handleSearch"
          />
          <el-select v-model="filters.source" placeholder="소스 선택" clearable size="default" @change="handleSearch">
            <el-option
              v-for="source in sourceOptions"
              :key="source.value"
              :label="source.label"
              :value="source.value"
            />
          </el-select>
        <el-button type="primary" :icon="Search" @click="handleSearch">검색</el-button>
        <el-button class="filter-order-button" plain @click="toggleOrder">{{ orderLabel }}</el-button>
        <el-button :icon="Refresh" @click="handleReload">새로고침</el-button>
      </div>

      <div class="content-body">
        <AppLoading v-if="loading" text="상품을 불러오는 중입니다..." />
        <template v-else>
          <div v-if="products.length" class="product-grid">
            <div
              v-for="product in products"
              :key="product.productid"
              class="product-card"
              @click="handleCardClick(product, $event)"
            >
              <div class="card-checkbox" @click.stop>
                <el-checkbox
                  :model-value="isSelected(product.productid)"
                  @change="value => toggleSelect(product.productid, value)"
                />
              </div>
              <div class="product-image">
                <img
                  :src="product.thumbnail || '/no-image.jpg'"
                  :alt="product.title || '상품 이미지'"
                  @error="handleImageError"
                  draggable="false"
                />
              </div>
              <div class="product-info">
                <div class="product-top-row">
                  <div class="product-status">
                    <el-tag v-if="product.status" :type="getStatusTagType(product.status)" size="small">
                      {{ product.status }}
                    </el-tag>
                  </div>
                  <span class="product-source">{{ formatSource(product.source) }} - {{ formatNumber(product.reviewCount ?? 0) }}</span>
                </div>
                <div class="product-market-row">
                  <div class="product-market-copy" @click.stop="copyMarketUrl(product.productUrl)">
                    <span class="product-market">{{ marketSignature(product.productUrl) }}</span>
                    <img
                      src="@/assets/icon_content_copy.svg"
                      alt="복사"
                      class="copy-icon"
                      title="클릭하여 복사"
                    />
                  </div>
                  <span class="product-date">{{ formatDateOnly(product.createdAt) }}</span>
                </div>
                <h4 class="product-title" :title="product.title">
                  {{ product.title || '-' }}
                </h4>
                <div class="product-meta">
                  <span class="product-price">{{ formatPrice(product.price) }}</span>
                  <div class="product-links">
                    <a
                      v-if="product.productUrl"
                      :href="product.productUrl"
                      target="_blank"
                      class="product-link"
                      @click.stop
                    >
                      원본
                    </a>
                    <a
                      v-if="product.smartstoreChannelProductNo"
                      :href="smartstoreUploadUrl(product.smartstoreChannelProductNo)"
                      target="_blank"
                      class="product-link"
                      @click.stop
                    >
                      업로드
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <el-empty v-else description="조건에 맞는 상품이 없습니다." />
        </template>
      </div>

      <footer class="main-footer">
        <div class="footer-left">
          <el-checkbox :model-value="isAllSelected" @change="toggleSelectAll">
            전체 상품 선택
          </el-checkbox>
          <span class="selected-count">선택 {{ selectedCount }}건</span>
        </div>
        <div class="footer-right">
          <el-pagination
            layout="total, sizes, prev, pager, next, jumper"
            :page-sizes="limitOptions"
            :total="pagination.total"
            v-model:current-page="pagination.page"
            :page-size="pagination.limit"
            @current-change="handlePageChange"
            @size-change="handleSizeChange"
          />
        </div>
      </footer>
    </div>

    <aside class="n2n-register__side">
      <div class="side-section">
        <div class="side-section__header">
          <h3>상태 요약</h3>
        </div>
        <div class="status-grid">
          <div v-for="item in displayedStatusCounts" :key="item.status" class="status-card">
            <div class="status-label">{{ item.status || '-' }}</div>
            <div class="status-value">{{ formatNumber(item.count) }}</div>
          </div>
        </div>
      </div>

      <div class="side-section">
        <div class="side-section__header">
          <h3>네이버 마켓</h3>
        </div>
        <el-select
          v-model="form.naverMarketNumber"
          placeholder="마켓을 선택하세요"
          clearable
          class="full-width"
        >
          <el-option
            v-for="account in naverAccounts"
            :key="account.naverMarketNumber"
            :label="naverAccountLabel(account)"
            :value="account.naverMarketNumber"
          />
        </el-select>
      </div>

      <div class="side-spacer" />

      <div class="side-section bottom-section">
        <div class="button-row">
          <el-button
            type="primary"
            size="large"
            :loading="submitLoading"
            :disabled="!canSubmit"
            @click="handleRegister"
          >
            등록
          </el-button>
          <el-button
            size="large"
            :loading="ipConfirmLoading"
            :disabled="!canIpConfirm"
            @click="handleIpConfirm"
          >
            {{ ipConfirmLabel }}
          </el-button>
          <el-button
            size="large"
            :disabled="!selectedCount"
            @click="openPriceChangeModal"
          >
            가격 변경
          </el-button>
          <el-button
            size="large"
            :loading="removalLoading"
            :disabled="!canRemoveFromMarket"
            @click="handleRemoveFromMarket"
          >
            마켓에서 내리기
          </el-button>
          <el-button
            size="large"
            :loading="deleteLoading"
            :disabled="!selectedCount"
            @click="handleDelete"
          >
            삭제
          </el-button>
        </div>
      </div>
    </aside>

    <N2nProductDetailModal
      v-model:visible="detailModal.visible"
      :productid="detailModal.productid"
    />
    <el-dialog v-model="priceChangeModal.visible" title="가격 변경" width="480px">
      <div class="price-change-row">
        <el-input
          class="price-change-input"
          v-model="priceChangeForm.value"
          placeholder="변경 값"
          type="number"
        />
        <el-select v-model="priceChangeForm.unitType" placeholder="단위" class="price-change-select">
          <el-option
            v-for="option in priceUnitOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </el-select>
        <el-select v-model="priceChangeForm.direction" placeholder="방향" class="price-change-select">
          <el-option
            v-for="option in priceDirectionOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </el-select>
      </div>
      <template #footer>
        <el-button @click="priceChangeModal.visible = false">취소</el-button>
        <el-button
          :loading="priceChangeLoading"
          :disabled="!canPriceChange"
          @click="handlePriceChange"
        >
          가격 변경
        </el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, Refresh } from '@element-plus/icons-vue';
import AppLoading from '../../components/common/loading.vue';
import N2nProductDetailModal from '../../components/modal/N2nProductDetailModal.vue';
import {
  fetchRegisterBasicInfo,
  searchRegisterProducts,
  postRegisterPriceChange,
  putRegisterPriceChange,
  postRegisterIpConfirm,
  deleteRegisterProducts,
  postN2nRemoval,
  fetchGroupCodes
} from '../../services/n2n';

export default {
  name: 'N2nProductRegistration',
  components: {
    AppLoading,
    N2nProductDetailModal
  },
  setup() {
    const loading = ref(false);
    const submitLoading = ref(false);
    const deleteLoading = ref(false);
    const removalLoading = ref(false);
    const priceChangeLoading = ref(false);
    const ipConfirmLoading = ref(false);
    const products = ref([]);
    const statusCounts = ref([]);
    const naverAccounts = ref([]);
    const groupOptions = ref([]);
    const selectedProductIds = ref([]);
    const pagination = reactive({
      page: 1,
      limit: 60,
      total: 0
    });
    const filters = reactive({
      status: '',
      groupid: '',
      order: 'default',
      q: '',
      source: '',
      marketNumber: ''
    });
    const form = reactive({
      naverMarketNumber: null,
    });
    const priceChangeForm = reactive({
      value: null,
      unitType: 'PERCENT',
      direction: 'DOWN'
    });
    const priceChangeModal = reactive({
      visible: false
    });
    const detailModal = reactive({
      visible: false,
      productid: null
    });

    const statusOptions = [
      { value: '수집대기중', label: '수집대기중' },
      { value: '수집중', label: '수집중' },
      { value: '이미지번역중', label: '이미지번역중' },
      { value: '수집실패', label: '수집실패' },
      { value: '수집성공', label: '수집성공' },
      { value: '지재권위험', label: '지재권 위험' },
      { value: '업로드중', label: '업로드중' },
      { value: '업로드실패', label: '업로드실패' },
      { value: '업로드성공', label: '업로드성공(판매x)' },
      { value: '판매됨', label: '업로드성공(판매o)' },
      { value: '마켓삭제중', label: '삭제요청중' },
      { value: '재등록대기', label: '재등록대기' },
      { value: '삭제추천', label: '삭제추천' },
      { value: '마켓삭제실패', label: '마켓삭제실패' },
      { value: '삭제함', label: '삭제함' }
    ];

    const statusSummaryOptions = statusOptions
      .filter(option => option.value !== '판매됨')
      .map(option =>
        option.value === '업로드성공'
          ? { ...option, label: '업로드성공' }
          : option
      );

    const sourceOptions = [
      { value: 'IS_NULL', label: '출처없음' },
      { value: 'bulsaja', label: 'bulsaja' },
      { value: 'dsell', label: 'dsell' },
      { value: 'heysellor', label: 'heysellor' },
      { value: 'pick2sell', label: 'pick2sell' },
      { value: 'sellerpick', label: 'sellerpick' },
      { value: 'sellforyou', label: 'sellforyou' },
      { value: 'windly', label: 'windly' },
      { value: 'percenty', label: 'percenty' },
      { value: 'wseller', label: 'wseller' },
      { value: 'proxyss', label: 'proxyss' }
    ];
    const priceUnitOptions = [
      { value: 'WON', label: '원' },
      { value: 'PERCENT', label: '퍼센트' }
    ];
    const priceDirectionOptions = [
      { value: 'UP', label: '인상' },
      { value: 'DOWN', label: '인하' }
    ];

    const limitOptions = [30,60,100,200,400];

    const selectedCount = computed(() => selectedProductIds.value.length);
    const isAllSelected = computed(() => {
      if (!products.value.length) return false;
      return selectedProductIds.value.length === products.value.length;
    });
    const displayedStatusCounts = computed(() => {
      return statusSummaryOptions.map(option => {
        const found = statusCounts.value.find(item => item.status === option.value);
        return {
          status: option.label,
          count: found ? found.count : 0
        };
      });
    });
    const selectedProducts = computed(() => {
      return products.value.filter(p => selectedProductIds.value.includes(p.productid));
    });
    const registerableStatuses = new Set([
      '수집성공',
      '지재권위험',
      '이미지번역중',
      '업로드중',
      '업로드실패',
      '재등록대기',
      '마켓삭제실패'
    ]);
    const removableStatuses = new Set(['업로드성공', '업로드실패']);
    const ipConfirmableStatuses = new Set(['지재권위험', '이미지번역중', '삭제함']);

    const allSelectedAreRegisterable = computed(() => {
      if (!selectedProducts.value.length) return false;
      return selectedProducts.value.every(p => registerableStatuses.has(p.status));
    });

    const allSelectedAreRemovable = computed(() => {
      if (!selectedProducts.value.length) return false;
      return selectedProducts.value.every(p => removableStatuses.has(p.status));
    });

    const ipConfirmSelectedStatus = computed(() => {
      if (!selectedProducts.value.length) return null;
      if (!selectedProducts.value.every(p => ipConfirmableStatuses.has(p.status))) return null;
      const status = selectedProducts.value[0]?.status;
      if (!status) return null;
      if (!selectedProducts.value.every(p => p.status === status)) return null;
      return status;
    });

    const canIpConfirm = computed(() => {
      return selectedProductIds.value.length > 0 && Boolean(ipConfirmSelectedStatus.value);
    });

    const ipConfirmLabel = computed(() => '수집성공으로전환');

    const canSubmit = computed(
      () =>
        selectedProductIds.value.length > 0 &&
        allSelectedAreRegisterable.value &&
        form.naverMarketNumber
    );

    const canRemoveFromMarket = computed(() => {
      return selectedProductIds.value.length > 0 && allSelectedAreRemovable.value;
    });
    const canPriceChange = computed(() => {
      const parsedValue = Number.parseInt(priceChangeForm.value, 10);
      return (
        selectedProductIds.value.length > 0 &&
        Number.isInteger(parsedValue) &&
        parsedValue > 0 &&
        priceChangeForm.unitType &&
        priceChangeForm.direction
      );
    });

    const groupLabel = group => {
      if (!group) return '-';
      return group.groupname ?? group.groupid ?? '-';
    };

    const formatSource = source => {
      if (!source || source === 'IS_NULL') return '출처없음';
      return source;
    };

    const marketSignature = url => {
      if (!url) return '-';
      try {
        const targetUrl = url.startsWith('http') ? url : `https://${url}`;
        const parsed = new URL(targetUrl);
        const segments = parsed.pathname.split('/').filter(Boolean);
        if (segments.length) return segments[0];
      } catch (e) {
        const match = url.match(/smartstore\.naver\.com\/([^/]+)/);
        if (match?.[1]) return match[1];
      }
      return '-';
    };

    const copyMarketUrl = async url => {
      const marketName = marketSignature(url);
      if (!marketName || marketName === '-') {
        ElMessage.warning('마켓명이 없습니다.');
        return;
      }
      const marketUrl = `https://smartstore.naver.com/${marketName}`;
      try {
        await navigator.clipboard.writeText(marketUrl);
        ElMessage.success('마켓 URL이 복사되었습니다.');
      } catch (error) {
        ElMessage.error('복사에 실패했습니다.');
      }
    };

    const formatPrice = value => {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (!Number.isFinite(num)) return value;
      return `${num.toLocaleString()}원`;
    };

    const formatDateOnly = value => {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const formatNumber = value => {
      const num = Number(value);
      if (!Number.isFinite(num)) return value ?? '-';
      return num.toLocaleString();
    };

    const smartstoreUploadUrl = smartstoreChannelProductNo => {
      if (!smartstoreChannelProductNo) return '';
      return `https://smartstore.naver.com/main/products/${smartstoreChannelProductNo}`;
    };

    const handleImageError = event => {
      event.target.src = '/no-image.jpg';
    };

    const getStatusTagType = status => {
      if (['수집실패', '업로드실패', '마켓삭제실패', '삭제함'].includes(status)) return 'danger';
      if (['지재권위험', '삭제추천'].includes(status)) return 'warning';
      if (
        ['수집성공', '업로드성공', '재등록대기'].includes(status)
      )
        return 'success';
      if (status === '판매됨') return 'warning';
      return 'info';
    };

    const naverAccountLabel = account => {
      if (!account) return '-';
      const memo = account.naverMarketMemo ? ` - ${account.naverMarketMemo}` : '';
      return `#${account.naverMarketNumber}${memo}`;
    };

    const buildSearchParams = () => {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (filters.marketNumber) params.marketNumber = filters.marketNumber;
      if (filters.status) params.status = filters.status;
      if (filters.groupid) params.groupid = filters.groupid;
      if (filters.order) params.order = filters.order;
      if (filters.q.trim()) params.q = filters.q.trim();
      if (filters.source === 'IS_NULL') params.source = 'null';
      else if (filters.source) params.source = filters.source;
      return params;
    };

    const orderLabel = computed(() => {
      if (filters.order === 'latest') return '최신순';
      if (filters.order === 'oldest') return '과거순';
      if (filters.order === 'review') return '리뷰순';
      return '기본순';
    });

    const toggleOrder = () => {
      if (filters.order === 'default') filters.order = 'latest';
      else if (filters.order === 'latest') filters.order = 'oldest';
      else if (filters.order === 'oldest') filters.order = 'review';
      else filters.order = 'default';
      handleSearch();
    };

    const syncSelections = () => {
      const ids = new Set(products.value.map(product => product.productid));
      selectedProductIds.value = selectedProductIds.value.filter(id => ids.has(id));
    };

    const loadProducts = async () => {
      loading.value = true;
      try {
        const params = buildSearchParams();
        const { data } = await searchRegisterProducts(params);
        const list = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : data?.items || [];
        products.value = list;
        const paginationInfo = data?.pagination;
        pagination.total = paginationInfo?.total ?? list.length;
        syncSelections();
      } catch (error) {
        products.value = [];
        pagination.total = 0;
        ElMessage.error(error.response?.data?.message || '상품을 불러오지 못했습니다.');
      } finally {
        loading.value = false;
      }
    };

    const loadBasicInfo = async () => {
      try {
        const { data } = await fetchRegisterBasicInfo();
        statusCounts.value = data?.data?.statusCounts ?? data?.statusCounts ?? [];
        naverAccounts.value = data?.data?.naverAccounts ?? data?.naverAccounts ?? [];
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '기본 정보를 불러오지 못했습니다.');
      }
    };

    const loadGroupOptions = async () => {
      try {
        const { data } = await fetchGroupCodes();
        groupOptions.value = data?.data ?? data ?? [];
      } catch (error) {
        groupOptions.value = [];
      }
    };

    const handleSearch = () => {
      pagination.page = 1;
      loadProducts();
    };

    const handleReload = async () => {
      await Promise.all([loadBasicInfo(), loadProducts()]);
    };

    const handlePageChange = page => {
      pagination.page = page;
      loadProducts();
    };

    const handleSizeChange = size => {
      pagination.limit = size;
      pagination.page = 1;
      loadProducts();
    };

    const toggleSelect = (productid, checked) => {
      if (checked) {
        if (!selectedProductIds.value.includes(productid)) {
          selectedProductIds.value = [...selectedProductIds.value, productid];
        }
      } else {
        selectedProductIds.value = selectedProductIds.value.filter(id => id !== productid);
      }
    };

    const toggleSelectAll = checked => {
      if (!products.value.length) return;
      if (checked) {
        selectedProductIds.value = products.value.map(product => product.productid);
      } else {
        selectedProductIds.value = [];
      }
    };

    const isSelected = productid => selectedProductIds.value.includes(productid);

    const handleCardClick = (product, event) => {
      if (!product) return;
      if (event?.target?.closest('.card-checkbox') || event?.target?.closest('.product-link')) {
        return;
      }
      openDetailModal(product.productid);
    };

    const openDetailModal = productid => {
      detailModal.productid = productid;
      detailModal.visible = true;
    };

    const handleRegister = async () => {
      if (!selectedProductIds.value.length) {
        ElMessage.warning('선택된 상품이 없습니다.');
        return;
      }
      if (!allSelectedAreRegisterable.value) {
        ElMessage.warning(
          '수집성공/지재권위험/이미지번역중/업로드중/업로드실패/재등록대기/마켓삭제실패 상태의 상품만 등록할 수 있습니다.'
        );
        return;
      }
      if (!form.naverMarketNumber) {
        ElMessage.warning('네이버 마켓을 선택하세요.');
        return;
      }
      const payload = {
        productids: selectedProductIds.value,
        naverMarketNumber: form.naverMarketNumber
      };
      submitLoading.value = true;
      try {
        const { data } = await postRegisterPriceChange(payload);
        ElMessage.success(data?.message || '등록 요청을 전송했습니다.');
        await loadBasicInfo();
        await loadProducts();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '등록 요청에 실패했습니다.');
      } finally {
        submitLoading.value = false;
      }
    };

    const handleDelete = async () => {
      if (!selectedProductIds.value.length) {
        ElMessage.warning('선택된 상품이 없습니다.');
        return;
      }
      deleteLoading.value = true;
      try {
        const { data } = await deleteRegisterProducts(selectedProductIds.value);
        ElMessage.success(data?.message || '삭제함 상태로 변경되었습니다.');
        selectedProductIds.value = [];
        await loadBasicInfo();
        await loadProducts();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '삭제 요청에 실패했습니다.');
      } finally {
        deleteLoading.value = false;
      }
    };

    const handleRemoveFromMarket = async () => {
      if (!selectedProductIds.value.length) {
        ElMessage.warning('선택된 상품이 없습니다.');
        return;
      }
      if (!allSelectedAreRemovable.value) {
        ElMessage.warning(
          '업로드성공/업로드실패 상태의 상품만 마켓에서 내릴 수 있습니다.'
        );
        return;
      }
      removalLoading.value = true;
      try {
        const { data } = await postN2nRemoval(selectedProductIds.value);
        ElMessage.success(data?.message || '마켓 삭제 요청이 등록되었습니다.');
        selectedProductIds.value = [];
        await loadBasicInfo();
        await loadProducts();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '마켓 삭제 요청에 실패했습니다.');
      } finally {
        removalLoading.value = false;
      }
    };

    const openPriceChangeModal = () => {
      if (!selectedProductIds.value.length) {
        ElMessage.warning('선택된 상품이 없습니다.');
        return;
      }
      priceChangeModal.visible = true;
    };

    const handleIpConfirm = async () => {
      if (!selectedProductIds.value.length) {
        ElMessage.warning('선택된 상품이 없습니다.');
        return;
      }
      if (!ipConfirmSelectedStatus.value) {
        ElMessage.warning('지재권위험/이미지번역중/삭제함 상태의 상품을 같은 상태끼리 선택하세요.');
        return;
      }

      const payload = {
        status: ipConfirmSelectedStatus.value,
        productids: selectedProductIds.value
      };

      ipConfirmLoading.value = true;
      try {
        const { data } = await postRegisterIpConfirm(payload);
        ElMessage.success(data?.message || '지식재산권 확인 완료 처리되었습니다.');
        selectedProductIds.value = [];
        await loadBasicInfo();
        await loadProducts();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '지식재산권 확인 완료 처리에 실패했습니다.');
      } finally {
        ipConfirmLoading.value = false;
      }
    };

    const handlePriceChange = async () => {
      if (!selectedProductIds.value.length) {
        ElMessage.warning('선택된 상품이 없습니다.');
        return;
      }
      const parsedValue = Number.parseInt(priceChangeForm.value, 10);
      if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
        ElMessage.warning('가격 변경 값은 1 이상의 정수여야 합니다.');
        return;
      }
      if (!priceChangeForm.unitType || !priceChangeForm.direction) {
        ElMessage.warning('가격 변경 단위와 방향을 선택하세요.');
        return;
      }
      const payload = {
        productids: selectedProductIds.value,
        value: parsedValue,
        unitType: priceChangeForm.unitType,
        direction: priceChangeForm.direction
      };
      priceChangeLoading.value = true;
      try {
        const { data } = await putRegisterPriceChange(payload);
        const summary = data?.data?.summary;
        if (summary) {
          const message = [
            `요청 ${formatNumber(summary.requested)}건`,
            `대상 ${formatNumber(summary.targets)}건`,
            `그룹 ${formatNumber(summary.groups)}건`,
            `성공 ${formatNumber(summary.successGroups)}건`,
            `실패 ${formatNumber(summary.failedGroups)}건`
          ].join(' / ');
          ElMessage.success(message);
        } else {
          ElMessage.success(data?.message || '가격 변경 요청을 전송했습니다.');
        }
        priceChangeModal.visible = false;
        await loadBasicInfo();
        await loadProducts();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '가격 변경 요청에 실패했습니다.');
      } finally {
        priceChangeLoading.value = false;
      }
    };

    onMounted(async () => {
      await Promise.all([loadBasicInfo(), loadGroupOptions()]);
      await loadProducts();
    });

    return {
      Search,
      Refresh,
      loading,
      submitLoading,
      deleteLoading,
      removalLoading,
      priceChangeLoading,
      ipConfirmLoading,
      products,
      naverAccounts,
      groupOptions,
      filters,
      pagination,
      limitOptions,
      statusOptions,
      sourceOptions,
      priceUnitOptions,
      priceDirectionOptions,
      form,
      priceChangeForm,
      priceChangeModal,
      selectedCount,
      isAllSelected,
      displayedStatusCounts,
      canSubmit,
      canRemoveFromMarket,
      canPriceChange,
      canIpConfirm,
      ipConfirmLabel,
      detailModal,
      orderLabel,
      toggleOrder,
      formatPrice,
      formatDateOnly,
      formatNumber,
      groupLabel,
      formatSource,
      marketSignature,
      copyMarketUrl,
      smartstoreUploadUrl,
      naverAccountLabel,
      handleImageError,
      getStatusTagType,
      isSelected,
      toggleSelect,
      toggleSelectAll,
      handleSearch,
      handlePageChange,
      handleSizeChange,
      handleCardClick,
      handleReload,
      handleRegister,
      handleDelete,
      handleRemoveFromMarket,
      openPriceChangeModal,
      handleIpConfirm,
      handlePriceChange
    };
  }
};
</script>

<style scoped>
.n2n-register {
  display: grid;
  grid-template-columns: 3fr 1.15fr;
  gap: 16px;
  height: calc(100vh - 80px);
  box-sizing: border-box;
  background: #f6f7fb;
}

.n2n-register__main {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--el-bg-color);
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 16px;
  min-width: 0;
  overflow: hidden;
}

.n2n-register__side {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: var(--el-bg-color);
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 16px;
  min-width: 280px;
  overflow-y: auto;
}

.side-spacer {
  flex: 1;
}

.bottom-section {
  border-top: 1px solid var(--el-border-color-lighter);
  padding-top: 12px;
}

.filter-grid {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

.filter-grid > *:nth-child(1),
.filter-grid > *:nth-child(2),
.filter-grid > *:nth-child(3),
.filter-grid > *:nth-child(5) {
  min-width: 140px;
  width: 140px;
}

.filter-grid > *:nth-child(4) {
  flex: 1;
  min-width: 200px;
}

.content-body {
  flex: 1;
  min-height: 0;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  padding: 0;
  background: #fff;
  overflow-y: auto;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  grid-auto-rows: min-content;
  gap: 16px;
  padding: 16px;
  box-sizing: border-box;
}

.product-card {
  position: relative;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  overflow: hidden;
  background-color: var(--el-bg-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.product-card:hover {
  border-color: var(--el-color-primary);
  box-shadow: 0 6px 20px rgba(64, 158, 255, 0.2);
  transform: translateY(-4px);
}

.card-checkbox {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 5;
  background-color: rgba(255, 255, 255, 0.98);
  border-radius: 8px;
  padding: 6px 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
}

.product-image {
  width: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  position: relative;
}

.product-image::before {
  content: '';
  display: block;
  padding-bottom: 100%;
}

.product-image img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-info {
  padding: 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
}

.product-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 8px;
}

.product-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.sourcing-candidate-check {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f0f9eb;
  border: 1px solid #d9ecbf;
  color: #67c23a;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  opacity: 0.9;
}

.product-date {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}

.product-market-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.product-market {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-market-copy {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  min-width: 0;
}

.copy-icon {
  width: 14px;
  height: 14px;
  opacity: 0.65;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.product-market-copy:hover .copy-icon {
  opacity: 1;
}

.product-source {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}

.product-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  align-self: stretch;
}

.product-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  margin-top: auto;
}

.product-price {
  font-weight: 700;
  font-size: 18px;
  color: var(--el-color-primary);
  letter-spacing: -0.5px;
}

.product-link {
  font-size: 11px;
  color: var(--el-text-color-placeholder);
  text-decoration: none;
  transition: color 0.2s;
  white-space: nowrap;
}

.product-link:hover {
  color: var(--el-color-primary);
}

.product-links {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.main-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #e4e7ed;
  flex-shrink: 0;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selected-count {
  color: var(--el-text-color-secondary);
  font-size: 13px;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
}

.side-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.side-section__header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.status-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.status-card {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 10px 12px;
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
}

.status-label {
  color: var(--el-text-color-secondary);
  font-size: 12px;
  line-height: 1.25;
}

.status-value {
  font-weight: 700;
  font-size: 17px;
  color: var(--el-color-primary);
  line-height: 1.15;
}

.button-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
}

.button-row .el-button {
  width: 100%;
  margin-left: 0;
}

.button-row .full-span {
  grid-column: 1 / -1;
}

.full-width {
  width: 100%;
}

.price-change-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.price-change-input {
  flex: 1 1 0;
  min-width: 140px;
}

.price-change-input .el-input-number {
  width: 100%;
}

.price-change-select {
  flex: 0 0 120px;
}


@media (max-width: 1200px) {
  .n2n-register {
    grid-template-columns: 1fr;
    height: auto;
  }

  .n2n-register__main,
  .n2n-register__side,
  .content-body {
    overflow: visible;
  }

  .content-body {
    flex: none;
    min-height: auto;
  }

  .n2n-register__side {
    max-height: none;
  }
}

@media (max-width: 768px) {
  .filter-grid {
    flex-wrap: wrap;
  }

  .filter-grid > * {
    min-width: 100% !important;
    width: 100% !important;
  }

  .price-change-row {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>